// React
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Amplify
import { getCurrentUser } from "aws-amplify/auth";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { selectAuthState, setAuthState } from "../../../Store/AuthSlice";
import {
  clearActiveAuthDomain,
  getActiveAuthDomain,
  setActiveAuthDomain,
} from "Auth/AuthDomain";

// Components
import SignUp from "../SignUp";
import SignIn from "../SignIn";
import SignedIn from "../SignedIn";
import ResetPassword from "../ResetPassword";
import { AuthHeader } from "../Components/AuthHeader";
import { AuthFooter } from "../Components/AuthFooter";

// Material UI
import { Tabs, Box } from "@mui/material";

// Constants
import { terracotta } from "../../../Constants/ColourConstants";
import { AUTH_DOMAINS } from "Constants/AuthConstants";

// Styles
import { styles, StyledTab } from "./styles";

const CustomTabPanel = (props) => {
  const { children, value, index, tabLabel, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`${tabLabel}-tab`}
      {...other}
    >
      {value === index && (
        <Box paddingLeft={0} paddingRight={0}>
          {children}
        </Box>
      )}
    </div>
  );
};

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
  tabLabel: PropTypes.string.isRequired,
};

const AuthContainer = () => {
  const { t } = useTranslation(); // Add translation hook
  const authState = useSelector(selectAuthState);
  const [tabValue, setTabValue] = useState(0);
  const dispatch = useDispatch();

  useEffect(() => {
    const checkCurrentUser = async () => {
      try {
        const user = await getCurrentUser();
        if (user) {
          setActiveAuthDomain(AUTH_DOMAINS.PRIMARY);
          dispatch(setAuthState("SignedIn"));
        }
      } catch {
        if (getActiveAuthDomain() === AUTH_DOMAINS.PRIMARY) {
          clearActiveAuthDomain();
        }
        dispatch(setAuthState("SignIn"));
      }
    };
    checkCurrentUser();
  }, [dispatch]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    dispatch(setAuthState(newValue === 0 ? "SignIn" : "SignUp"));
  };

  const SigninSignup = () => {
    return (
      <>
        <Box sx={styles.contentBox} gap={tabValue === 0 ? "6vh" : "2vh"}>
          <Box>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="Sign in and Sign up Tabs"
              variant="fullWidth"
              TabIndicatorProps={{ sx: { backgroundColor: terracotta } }}
            >
              <StyledTab index={0} label={t("auth.signIn")} />
              <StyledTab index={1} label={t("auth.signUp")} />
            </Tabs>
          </Box>
          <CustomTabPanel value={tabValue} index={0} tabLabel="sign-in">
            <SignIn />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={1} tabLabel="sign-up">
            <SignUp />
          </CustomTabPanel>
        </Box>
      </>
    );
  };

  switch (authState) {
    case "SignedIn":
      return <SignedIn />;
    case "ResetPassword":
      return (
        <Box xs={12} md={6} sx={styles.authBox}>
          <AuthHeader />
          <ResetPassword />
          <AuthFooter />
        </Box>
      );
    default:
      return (
        <Box xs={12} md={6} sx={styles.authBox}>
          <AuthHeader />
          <SigninSignup />
          <AuthFooter />
        </Box>
      );
  }
};

export default AuthContainer;
